import { easeCubicInOut } from 'd3-ease';
import { interpolate } from 'd3-interpolate';
import { select } from 'd3-selection';
import 'd3-transition';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef } from 'react';

import { CHART_TRANSITION_MS, getBaselinePy, pointTime } from '../utils/chartTransition';

function positionsEqual(a, b) {
  return Math.abs(a.px - b.px) < 0.01 && Math.abs(a.py - b.py) < 0.01;
}

function animateVerticalEntry(selection, target, yScaleSet) {
  const baselinePy = getBaselinePy(yScaleSet);
  selection
    .attr('transform', `translate(${target.px},${baselinePy})`)
    .attr('opacity', 0)
    .transition()
    .duration(CHART_TRANSITION_MS)
    .ease(easeCubicInOut)
    .attr('opacity', 1)
    .attrTween('transform', () => {
      const iy = interpolate(baselinePy, target.py);
      return t => `translate(${target.px},${iy(t)})`;
    });
}

function fadeInAtTarget(selection, target) {
  selection
    .attr('transform', `translate(${target.px},${target.py})`)
    .attr('opacity', 0)
    .transition()
    .duration(CHART_TRANSITION_MS)
    .ease(easeCubicInOut)
    .attr('opacity', 1);
}

function animateFromPrev(selection, prev, target) {
  selection
    .attr('transform', `translate(${prev.px},${prev.py})`)
    .attr('opacity', 1)
    .transition()
    .duration(CHART_TRANSITION_MS)
    .ease(easeCubicInOut)
    .attrTween('transform', () => {
      const ix = interpolate(prev.px, target.px);
      const iy = interpolate(prev.py, target.py);
      return t => `translate(${ix(t)},${iy(t)})`;
    });
}

/** Same index, different time bucket — move vertically at target x only. */
function animateVerticalOnly(selection, prevPy, target) {
  selection
    .attr('transform', `translate(${target.px},${prevPy})`)
    .attr('opacity', 1)
    .transition()
    .duration(CHART_TRANSITION_MS)
    .ease(easeCubicInOut)
    .attrTween('transform', () => {
      const iy = interpolate(prevPy, target.py);
      return t => `translate(${target.px},${iy(t)})`;
    });
}

function AnimatedDot({
  index,
  xTime,
  x,
  y,
  xScaleSet,
  yScaleSet,
  prevPositionsRef,
  hasHistoryRef,
  dotCount,
  animate,
  className,
  children,
}) {
  const gRef = useRef(null);

  useLayoutEffect(() => {
    const el = gRef.current;
    if (!el) {
      return;
    }

    const target = {
      px: xScaleSet(x),
      py: yScaleSet(y),
    };
    const prev = prevPositionsRef.current[index];
    const selection = select(el).interrupt();

    if (!animate) {
      selection.attr('transform', `translate(${target.px},${target.py})`).attr('opacity', 1);
      return;
    }

    if (prev && positionsEqual(prev, target)) {
      selection.attr('transform', `translate(${target.px},${target.py})`).attr('opacity', 1);
      return;
    }

    if (!hasHistoryRef.current) {
      animateVerticalEntry(selection, target, yScaleSet);
      return;
    }

    if (!prev) {
      fadeInAtTarget(selection, target);
      return;
    }

    // Same number of dots but bucket at this index changed — avoid sideways jump
    if (prev.xTime !== xTime && prevPositionsRef.current.length === dotCount) {
      animateVerticalOnly(selection, prev.py, target);
      return;
    }

    animateFromPrev(selection, prev, target);
  }, [
    index,
    xTime,
    x,
    y,
    xScaleSet,
    yScaleSet,
    prevPositionsRef,
    hasHistoryRef,
    dotCount,
    animate,
  ]);

  return (
    <g ref={gRef} className={className}>
      {children}
    </g>
  );
}

AnimatedDot.propTypes = {
  index: PropTypes.number.isRequired,
  xTime: PropTypes.number.isRequired,
  x: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.number]).isRequired,
  y: PropTypes.number.isRequired,
  xScaleSet: PropTypes.func.isRequired,
  yScaleSet: PropTypes.func.isRequired,
  prevPositionsRef: PropTypes.shape({ current: PropTypes.array }).isRequired,
  hasHistoryRef: PropTypes.shape({ current: PropTypes.bool }).isRequired,
  dotCount: PropTypes.number.isRequired,
  animate: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

function AnimatedDotSeries({
  dots,
  line,
  data,
  xScaleSet,
  yScaleSet,
  animate,
  dotClassName,
  locale,
  dotGroupClassName,
  dotClassNameStyles,
  dotEmptyClassName,
  labelClassName,
}) {
  const prevPositionsRef = useRef([]);
  const hasHistoryRef = useRef(false);

  useLayoutEffect(() => {
    prevPositionsRef.current = dots.map(d => ({
      px: xScaleSet(d.x),
      py: yScaleSet(d.y),
      xTime: pointTime(d.x),
    }));
    hasHistoryRef.current = true;
  }, [dots, xScaleSet, yScaleSet]);

  return (
    <>
      {dots.map((d, index) => {
        const point = data[d.i];
        const pointHasResponses =
          !Object.prototype.hasOwnProperty.call(point, 'responded') ||
          point.responded + point.commented > 0;

        return (
          <AnimatedDot
            key={`${line.field}-${index}`}
            index={index}
            xTime={pointTime(d.x)}
            x={d.x}
            y={d.y}
            xScaleSet={xScaleSet}
            yScaleSet={yScaleSet}
            prevPositionsRef={prevPositionsRef}
            hasHistoryRef={hasHistoryRef}
            dotCount={dots.length}
            animate={animate}
            className={dotGroupClassName}
          >
            {line.symbol !== 'circle' ? null : (
              <circle
                stroke={line.color}
                className={`${dotClassName} ${dotClassNameStyles} ${pointHasResponses ? '' : dotEmptyClassName}`}
                cx={0}
                cy={0}
                r={6}
              />
            )}
            {!line.hideLabels && (
              <text fill={line.color} y={-12} className={labelClassName}>
                {Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(d.y)}
              </text>
            )}
          </AnimatedDot>
        );
      })}
    </>
  );
}

AnimatedDotSeries.propTypes = {
  dots: PropTypes.arrayOf(PropTypes.object).isRequired,
  line: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  xScaleSet: PropTypes.func.isRequired,
  yScaleSet: PropTypes.func.isRequired,
  animate: PropTypes.bool,
  dotClassName: PropTypes.string,
  locale: PropTypes.string.isRequired,
  dotGroupClassName: PropTypes.string,
  dotClassNameStyles: PropTypes.string,
  dotEmptyClassName: PropTypes.string,
  labelClassName: PropTypes.string,
};

export default AnimatedDotSeries;
